<script>
import {
  captureTextSelection,
  applyTextHighlights,
  clearTextHighlights,
  scrollToHighlight,
} from '../../utils/textSelection';

export default {
  name: 'TxtViewer',
  props: {
    file: {
      type: Object,
      required: true,
    },
    annotations: {
      type: Array,
      default: () => [],
    },
    usersColor: {
      type: Object,
      default: () => ({}),
    },
    activeAnnotationId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      textContent: '',
      selectionPopover: null,
      pendingSelection: null,
    };
  },
  watch: {
    annotations: {
      handler() {
        this.$nextTick(() => this.renderHighlights());
      },
      deep: true,
    },
    usersColor: {
      handler() {
        this.$nextTick(() => this.renderHighlights());
      },
      deep: true,
    },
    activeAnnotationId(newId) {
      if (newId && this.$refs.txtContainer) {
        scrollToHighlight(this.$refs.txtContainer, newId);
      }
    },
  },
  methods: {
    async loadDocument() {
      this.loading = true;
      this.error = null;

      try {
        // originalFileKey already has CDN prefix applied by updateCdnLinks in proofView.v2
        const cdnUrl = this.file.originalFileKey;

        const response = await fetch(cdnUrl);
        if (!response.ok) throw new Error('Failed to fetch document');

        this.textContent = await response.text();
        this.loading = false;
        this.$emit('loaded', this.file);

        this.$nextTick(() => this.renderHighlights());
      } catch (err) {
        this.error = err.message || 'Failed to load document';
        this.loading = false;
        console.error('TxtViewer: Failed to load document', err);
      }
    },

    handleMouseUp() {
      this.processSelection(10);
    },

    handleTouchEnd() {
      // Longer delay for mobile — browser needs more time to finalize touch selection
      this.processSelection(300);
    },

    processSelection(delay) {
      setTimeout(() => {
        const container = this.$refs.txtContainer;
        if (!container) return;

        const selectionData = captureTextSelection(container);
        this.hideSelectionPopover();

        if (selectionData) {
          this.pendingSelection = selectionData;
          this.showSelectionPopover();
        }
      }, delay);
    },

    showSelectionPopover() {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = this.$refs.txtContainer.getBoundingClientRect();

      let left = rect.left - containerRect.left + (rect.width / 2) - 45;
      // Clamp so popover stays within container bounds
      const popoverWidth = 100;
      left = Math.max(4, Math.min(left, containerRect.width - popoverWidth - 4));

      this.selectionPopover = {
        top: rect.top - containerRect.top - 40,
        left,
      };
    },

    hideSelectionPopover() {
      this.selectionPopover = null;
    },

    handleAddComment() {
      if (!this.pendingSelection) return;
      this.$emit('text-selected', this.pendingSelection);
      this.hideSelectionPopover();
      this.pendingSelection = null;
      window.getSelection().removeAllRanges();
    },

    renderHighlights() {
      const container = this.$refs.txtContainer;
      if (!container) return;

      const textAnnotations = (this.annotations || []).filter(
        a => a.type === 'text' && a.textSelectionStart != null
      );

      applyTextHighlights(container, textAnnotations, this.usersColor, (annotationId) => {
        this.$emit('highlight-clicked', annotationId);
      });
    },

    scrollToAnnotation(annotationId) {
      if (this.$refs.txtContainer) {
        scrollToHighlight(this.$refs.txtContainer, annotationId);
      }
    },
  },
  mounted() {
    this.loadDocument();
  },
  beforeDestroy() {
    if (this.$refs.txtContainer) {
      clearTextHighlights(this.$refs.txtContainer);
    }
  },
};
</script>

<template>
  <div class="txt-viewer-wrapper" ref="txtWrapper">
    <div v-if="loading" class="d-flex align-items-center justify-content-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="sr-only">Loading document...</span>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger m-3">
      {{ error }}
    </div>

    <div
      v-if="!loading && !error"
      ref="txtContainer"
      class="txt-container"
      @mouseup="handleMouseUp"
      @touchend="handleTouchEnd"
    >{{ textContent }}</div>

    <!-- Selection popover for adding comments -->
    <div
      v-if="selectionPopover"
      class="selection-popover"
      :style="{ top: selectionPopover.top + 'px', left: selectionPopover.left + 'px' }"
    >
      <button
        class="btn btn-sm btn-success"
        @click.stop="handleAddComment"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
        Comment
      </button>
    </div>
  </div>
</template>

<style scoped>
.txt-viewer-wrapper {
  position: relative;
  width: 100%;
  overflow: auto;
}

.txt-container {
  white-space: pre-wrap;
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 14px;
  line-height: 1.6;
  padding: 40px 60px;
  background: white;
  color: #1a1a1a;
  min-height: 200px;
  tab-size: 4;
}

@media (max-width: 767px) {
  .txt-container {
    padding: 16px 12px;
    font-size: 13px;
    word-break: break-word;
  }
}

.selection-popover {
  position: absolute;
  z-index: 1000;
  animation: fadeIn 0.15s ease-in;
}

.selection-popover .btn {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
